import { useRef, useEffect, useCallback, useState, useMemo } from 'react'
import { cn, transformRAGSources, sanitizeTextForTTS } from '@/utils'
import { useChatStore, usePersonaStore, useSettingsStore, defaultRagConfig } from '@/stores'
import type { RAGSource, DisplaySource } from '@/types/source'
import { getRagConnector } from '@/services'
import type { ApprovalRequiredEvent, ReplyCut, ToolCallDelta, ToolResultEvent } from '@/services/ragConnector'
import type { ReplyWorkflow } from '@/utils/workflowReplyText'
import { CONTINUE_LABEL, continueMessage, continueQuestion } from '@/utils/replyCutText'
import { createVoiceTrackRouter } from '@/utils/voiceTrack'
import { useFeedback, useTextToSpeech, useSpeechToText, useStreamingTTS, useSourceUrl, useConversations, useVoiceConversation, useResizablePanel, useFeature } from '@/hooks'
import { useEffectiveTtsEnabled } from '@/hooks/useEffectiveTtsEnabled'
import { useRagProxyBase, getRagProxyBase } from '@/hooks/useRagProxyBase'
import { useEdgeLocalConfig } from '@/hooks/useEdgeLocalConfig'
import { useLocalTTS } from '@/hooks/useLocalTTS'
import { localTtsMode } from './localTtsMode'
import { ensureDictionary, type PronunciationDictionaryLocator } from '@/services/pronunciationDictionary'
import { composePronunciationLocators, resolveLibraryLocators } from '@/services/pronunciationLibrary'
import { apiClient } from '@/services/apiClient'
import { voiceConfigToApi } from '@/utils/voiceConfigMapper'
import {
  attachmentKind,
  createUploadFormData,
  resolveAttachmentMime,
} from '@/constants/attachments'
import { ChatInput } from './ChatInput'
import { SourcePanelToggle } from './SourcePanelToggle'
import { ExperienceMessageBubble } from './experience/ExperienceMessageBubble'
import { ExperienceSourcePanel } from './experience/ExperienceSourcePanel'
import { ExperienceSourceViewer } from './experience/ExperienceSourceViewer'
import { VoiceControlBar } from './VoiceControlBar'
import { VoiceConversationButton } from './VoiceConversationButton'
import { VoiceTranscriptOverlay } from './VoiceTranscriptOverlay'
import { ApprovalToast } from './ApprovalToast'
import { MessageSquare } from 'lucide-react'
import { useToast, Button } from '@/components/ui'
import { useApprovalToastStore } from '@/stores/approvalToastStore'
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalDescription,
  ModalBody,
  ModalFooter,
} from '@/components/ui/Modal'

/** What one send has made so far, so its end can be settled however it ends. */
interface SendRun {
  conversationId: string | null
  assistantMessageId: string | null
  /** The reply's stream reached onComplete, which flushed its TTS. */
  completed: boolean
  /** Ends the TTS reply prepared for this send, without its tail. */
  finishTts: (() => void) | null
  /** Voice is off: no TTS reply is prepared, so the turn's speech is over when the send ends. */
  voiceOff: boolean
  /** The whole-message voice (useTextToSpeech) speaks this send's reply, once it has finished (SA-1435). */
  wholeMessage: boolean
  /** The whole-message voice was handed the reply: its own end ends the turn. */
  spoken: boolean
  /** Voice Off or Stop speaking came mid-reply: nothing more of it is spoken (SA-1435). */
  speechStopped: boolean
}

/** How one send differs from a typed one. */
interface SendOptions {
  /**
   * The turn sent for this message when it differs from what is shown and
   * saved: a Continue shows and saves "Continue" and sends the longer ask
   * (SA-1241). Such a send never takes the composer's staged file.
   */
  completionMessage?: string
}

/**
 * End a send's turn the way Stop does, if its reply is still streaming
 * (SA-1395). A normal end or an error has already done this, so it does
 * nothing then; after any cancel (Stop, a barge-in, the shared connector
 * cancelled elsewhere) no callback fires, and this is where the turn ends.
 */
function endTurnIfStreaming(run: SendRun): void {
  const { conversationId, assistantMessageId } = run
  if (!conversationId || !assistantMessageId) return
  const state = useChatStore.getState()
  const reply = state.conversations[conversationId]?.messages.find((m) => m.id === assistantMessageId)
  if (reply?.isStreaming) {
    state.updateMessage(conversationId, assistantMessageId, { isStreaming: false })
  }
  if (state.isStreaming && state.streamingMessageId === assistantMessageId) {
    state.setStreaming(false)
  }
}

interface ChatContainerProps {
  className?: string
  /** Show voice controls bar */
  showVoiceControls?: boolean
}

export function ChatContainer({ className, showVoiceControls = true }: ChatContainerProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const [activeSources, setActiveSources] = useState<DisplaySource[]>([])
  const [selectedSource, setSelectedSource] = useState<DisplaySource | null>(null)
  const [isViewerOpen, setIsViewerOpen] = useState(false)
  const isSendingRef = useRef(false) // Guard against double-send race condition
  // The send in flight, so Voice Off or Stop speaking can silence the rest of its reply (SA-1435)
  const currentRunRef = useRef<SendRun | null>(null)
  const [showSwitchToSttDialog, setShowSwitchToSttDialog] = useState(false)
  const [suggestedQuestions, setSuggestedQuestions] = useState<string[]>([])
  const [pendingInputMessage, setPendingInputMessage] = useState<string>('')

  // Refs for bridging streaming TTS events to voice conversation engine
  const voiceTTSStartedRef = useRef<() => void>(() => {})
  const voiceTTSFinishedRef = useRef<() => void>(() => {})


  const {
    currentConversationId,
    conversations,
    isStreaming,
    streamingMessageId,
    createConversation,
    addMessage,
    appendToMessage,
    updateMessage,
    removeMessage,
    setStreaming,
  } = useChatStore()

  const { personas } = usePersonaStore()
  const { submitMessageFeedback } = useFeedback()

  // Conversations hook for backend sync
  const {
    createConversation: createConversationOnBackend,
    syncMessageToBackend,
    syncAttachmentMessage,
  } = useConversations()

  // Settings store for voice preferences, connection settings, and UI
  const {
    connections,
    voiceFeatures,
    voiceConversation: voiceConversationSettings,
    setSttEnabled,
    setSelectedVoiceId,
    getElevenLabsKey,
  } = useSettingsStore()

  // Deployment-level RAG base URL (replaces the legacy connections.ragServerUrl
  // user setting; populated from /api/features.rag_proxy_base).
  const ragProxyBase = useRagProxyBase()

  // Edge-local config (for Jetson Thor / edge deployments with local STT/TTS)
  const { config: edgeLocalConfig, localTts } = useEdgeLocalConfig()

  const [experienceSourcesVisible, setExperienceSourcesVisible] = useState(true)

  // Conversation-level STT language override (null = use persona/user default).
  // Resets when the user switches to a different conversation.
  const [sttLanguageOverride, setSttLanguageOverride] = useState<string | null>(null)
  useEffect(() => {
    setSttLanguageOverride(null)
  }, [currentConversationId])

  // Resizable Experience source panel
  const experiencePanel = useResizablePanel({
    defaultSize: 320,
    minSize: 250,
    maxSize: 600,
    storageKey: 'experience-source-panel-width',
    side: 'right',
  })

  const currentConversation = currentConversationId
    ? conversations[currentConversationId]
    : null

  // Restore activeSources from the last assistant message when returning to a conversation.
  // Uses a ref to track the last restored conversation so we don't re-run during streaming.
  const lastRestoredConvRef = useRef<string | null>(null)
  useEffect(() => {
    if (!currentConversation || !currentConversationId) return
    if (isStreaming) return
    // Already restored for this conversation
    if (lastRestoredConvRef.current === currentConversationId && activeSources.length > 0) return

    const messages = currentConversation.messages
    if (messages.length === 0) return

    // Walk backwards to find the last assistant message with sources
    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i]
      if (msg.role === 'assistant' && msg.sources && msg.sources.length > 0) {
        const displaySources = transformRAGSources(msg.sources as RAGSource[])
        setActiveSources(displaySources)
        setExperienceSourcesVisible(true)
        lastRestoredConvRef.current = currentConversationId
        return
      }
    }
  }) // Run on every render — the guards prevent unnecessary work

  // Get persona from the current conversation, not global state
  const conversationPersonaId = currentConversation?.personaId
  const activePersona = conversationPersonaId ? personas[conversationPersonaId] : null
  const ragConfig = activePersona?.ragConfig ?? defaultRagConfig

  // Webchat attachments (chat_attachments feature; persona-bound only —
  // the personaless legacy path never reaches the backend, spec §2)
  const attachmentsFlagOn = useFeature('chat_attachments')
  const attachmentsEnabled = attachmentsFlagOn && !!activePersona
  const [stagedFile, setStagedFile] = useState<File | null>(null)
  const [attachmentError, setAttachmentError] = useState<string | null>(null)
  const [isPreparingAttachment, setIsPreparingAttachment] = useState(false)

  // ChatContainer stays mounted across conversation switches — drop any staged
  // file when the conversation changes, or a file picked in conversation A would
  // silently upload into B on the next send.
  useEffect(() => {
    setStagedFile(null)
    setAttachmentError(null)
  }, [currentConversationId])

  const handleStageFile = useCallback((file: File) => {
    if (!resolveAttachmentMime(file)) {
      setAttachmentError("This file type isn't supported.")
      return
    }
    setAttachmentError(null)
    setStagedFile(file)
  }, [])

  const handleRemoveStagedFile = useCallback(() => {
    setStagedFile(null)
    setAttachmentError(null)
  }, [])

  // Also reset the STT language override when the persona changes —
  // currentConversationId alone does not always change on persona switch
  // (e.g. when the user navigates to a persona without selecting a conversation).
  useEffect(() => {
    setSttLanguageOverride(null)
  }, [conversationPersonaId])

  // Cache for resolved system prompts — keyed by personaId (the
  // P1.T9 composer-render path). P4.T6 retired the legacy
  // PromptConfig-render fallback that was also cached here.
  const promptCacheRef = useRef<Record<string, string>>({})

  // v2 runtime is core — the persona-level composer render is the
  // default whenever a persona is bound. The endpoint handles both
  // bundle-backed personas (composer renders bundle.blocks) and legacy
  // personas (composer falls through to rag_config.system_prompt via
  // BC). Direct-to-RAG (rc.systemPrompt) remains only for personaless
  // chat (no persona bound) or if the render-prompt call fails.

  // Resolve the system prompt for a given ragConfig. Reads fresh from params, not closures.
  async function fetchSystemPrompt(
    rc: typeof ragConfig,
    personaId: string | null
  ): Promise<string> {
    if (personaId) {
      const cacheKey = `persona:${personaId}`
      if (promptCacheRef.current[cacheKey]) return promptCacheRef.current[cacheKey]
      try {
        const data: any = await apiClient.get(
          `/api/personas/${personaId}/render-prompt`
        )
        const rendered = data.rendered_prompt ?? rc.systemPrompt ?? ''
        promptCacheRef.current[cacheKey] = rendered
        return rendered
      } catch {
        // BC: on any failure (auth, network, 404), fall back to the
        // direct rc.systemPrompt read below so chat never breaks.
      }
    }

    return rc.systemPrompt
  }

  // Toast notifications (declared early so pronunciation useEffect can use it)
  const { addToast } = useToast()

  // Pronunciation dictionary locators resolved from persona rules
  const [pronunciationLocators, setPronunciationLocators] = useState<PronunciationDictionaryLocator[] | undefined>()
  // Locators for shared library dictionaries (resolved server-side)
  const [libraryLocators, setLibraryLocators] = useState<PronunciationDictionaryLocator[]>([])
  const { updatePersona: updatePersonaInStore } = usePersonaStore()
  const { hasElevenLabsKey } = useSettingsStore()
  // Gates the library-locator resolution effect below: on deployment
  // profiles without the `voice` feature, the resolve-locators router
  // isn't registered, so calling it would 404 on every activation.
  const voiceEnabled = useFeature('voice')

  // Lazily resolve pronunciation dictionary when persona rules change.
  // hasElevenLabsKey is in deps so the effect re-runs when the key becomes available.
  useEffect(() => {
    const rules = activePersona?.voiceConfig?.pronunciationRules
    if (!rules || rules.length === 0 || activePersona?.voiceConfig?.provider !== 'elevenlabs') {
      setPronunciationLocators(undefined)
      return
    }

    const apiKey = getElevenLabsKey()
    if (!apiKey) {
      setPronunciationLocators(undefined)
      return
    }

    let cancelled = false

    ensureDictionary(
      apiKey,
      activePersona.id,
      activePersona.name,
      rules,
      activePersona.voiceConfig?.pronunciationDictionary,
    )
      .then(({ ref, locator }) => {
        if (cancelled) return
        setPronunciationLocators([locator])

        // Cache the dictionary reference for reuse (local store + backend)
        const isNewRef =
          !activePersona.voiceConfig?.pronunciationDictionary ||
          activePersona.voiceConfig.pronunciationDictionary.rulesHash !== ref.rulesHash

        if (isNewRef) {
          const updatedVoiceConfig = {
            ...activePersona.voiceConfig!,
            pronunciationDictionary: ref,
          }

          // Update local store immediately
          updatePersonaInStore(activePersona.id, {
            voiceConfig: updatedVoiceConfig,
          })

          // Persist to backend so the ref survives page reloads
          const persistBody = {
            voice_config: voiceConfigToApi(updatedVoiceConfig),
          }
          apiClient.fetch(`/api/personas/${activePersona.id}`, {
            method: 'PUT',
            body: JSON.stringify(persistBody),
          }).then(resp => {
            if (!resp.ok) {
              resp.text().then(text => console.warn('[ChatContainer] Failed to persist pronunciation dictionary ref:', resp.status, text))
            }
          }).catch(err => {
            console.warn('[ChatContainer] Failed to persist pronunciation dictionary ref:', err)
          })
        }
      })
      .catch((err) => {
        if (cancelled) return
        console.error('[ChatContainer] Failed to resolve pronunciation dictionary:', err)
        setPronunciationLocators(undefined)
        addToast({
          type: 'error',
          title: 'Pronunciation Setup Failed',
          description: 'Could not set up pronunciation rules. TTS will proceed without custom pronunciation.',
          duration: 8000,
        })
      })

    return () => { cancelled = true }
  }, [
    activePersona?.id,
    activePersona?.name,
    activePersona?.voiceConfig?.provider,
    activePersona?.voiceConfig?.pronunciationRules,
    activePersona?.voiceConfig?.pronunciationDictionary,
    hasElevenLabsKey,
    getElevenLabsKey,
    updatePersonaInStore,
    addToast,
  ])

  // Resolve shared library dictionaries selected on the persona (resolution
  // happens server-side, so no browser ElevenLabs API key is needed here).
  useEffect(() => {
    const ids = activePersona?.voiceConfig?.pronunciationDictionaryIds
    if (!voiceEnabled || !ids?.length || activePersona?.voiceConfig?.provider !== 'elevenlabs') {
      setLibraryLocators([])
      return
    }

    let cancelled = false
    resolveLibraryLocators(ids)
      .then(({ locators, skipped }) => {
        if (cancelled) return
        setLibraryLocators(locators)
        if (skipped.length > 0 && locators.length === 0) {
          addToast({
            type: 'error',
            title: 'Pronunciation Dictionaries Unavailable',
            description:
              'TTS will proceed without the shared pronunciation dictionaries.',
            duration: 8000,
          })
        }
      })
      .catch((err) => {
        if (cancelled) return
        console.warn('[ChatContainer] Failed to resolve library dictionaries:', err)
        setLibraryLocators([])
        addToast({
          type: 'error',
          title: 'Pronunciation Dictionaries Unavailable',
          description:
            'TTS will proceed without the shared pronunciation dictionaries.',
          duration: 8000,
        })
      })

    return () => {
      cancelled = true
    }
  }, [
    activePersona?.id,
    activePersona?.voiceConfig?.provider,
    activePersona?.voiceConfig?.pronunciationDictionaryIds,
    voiceEnabled,
    addToast,
  ])

  // Rules locator first (wins on first-match), then library dictionaries.
  const combinedLocators = useMemo(() => {
    const combined = composePronunciationLocators(
      pronunciationLocators?.[0],
      libraryLocators,
    )
    return combined.length > 0 ? combined : undefined
  }, [pronunciationLocators, libraryLocators])

  // Fetch suggested questions when persona changes
  useEffect(() => {
    const fetchSuggestions = async () => {
      console.debug('[ChatContainer] Fetching suggested questions, activePersona:', activePersona?.id, activePersona?.name)

      if (!activePersona) {
        console.debug('[ChatContainer] No activePersona, clearing suggestions')
        setSuggestedQuestions([])
        return
      }

      const serverUrl = activePersona.ragConfig.serverUrl || ragProxyBase
      const apiKey = activePersona.ragConfig.apiKey || connections.ragApiKey
      const graphId = activePersona.ragConfig.graphId

      console.debug('[ChatContainer] Using serverUrl:', serverUrl, 'apiKey present:', !!apiKey, 'graphId:', graphId)

      if (!serverUrl) {
        console.debug('[ChatContainer] No serverUrl configured, clearing suggestions')
        setSuggestedQuestions([])
        return
      }

      try {
        const connector = getRagConnector()
        const questions = await connector.fetchSuggestedQuestions(serverUrl, apiKey, graphId)
        console.debug('[ChatContainer] Fetched suggested questions:', questions.length)
        setSuggestedQuestions(questions)
      } catch (error) {
        console.warn('[ChatContainer] Failed to fetch suggested questions:', error)
        setSuggestedQuestions([])
      }
    }

    fetchSuggestions()
  }, [activePersona?.id, activePersona?.ragConfig.serverUrl, activePersona?.ragConfig.apiKey, activePersona?.ragConfig.graphId, ragProxyBase, connections.ragApiKey])

  // Handler for suggested question clicks
  const handleSuggestionClick = useCallback((questionText: string) => {
    setPendingInputMessage(questionText)
  }, [])

  // TTS hook
  const {
    isEnabled: ttsEnabled,
    setEnabled: setTtsEnabledLocal,
    isSpeaking: ttsSpeaking,
    speak,
    stop: stopSpeaking,
    isReady: ttsReady,
    voices,
    selectedVoiceId,
    setVoiceId,
  } = useTextToSpeech({
    voiceId: voiceFeatures.selectedVoiceId || undefined,
    modelId: activePersona?.voiceConfig?.modelId,
    pronunciationDictionaryLocators: combinedLocators,
    onStart: () => voiceTTSStartedRef.current(),
    onEnd: () => voiceTTSFinishedRef.current(),
    onError: (error) => console.error('[TTS Error]:', error),
  })

  // Effective TTS state — for chat-only personas, the persona's mode is a
  // HINT that defaults TTS off (user can still toggle on; choice persists
  // per-persona via settings sync). For voice/avatar personas, the global
  // `voiceFeatures.ttsEnabled` is the source of truth (today's behavior).
  // See hooks/useEffectiveTtsEnabled.ts.
  const { effectiveTtsEnabled, setEffectiveTtsEnabled } =
    useEffectiveTtsEnabled(
      conversationPersonaId ?? null,
      activePersona?.presentationMode ?? null,
    )

  // Sync TTS enabled state with the *effective* value (was: global setting).
  useEffect(() => {
    setTtsEnabledLocal(effectiveTtsEnabled)
  }, [effectiveTtsEnabled, setTtsEnabledLocal])

  // Track whether we've set an initial voice to avoid repeated updates
  const voiceInitializedRef = useRef(false)

  // When the active persona changes, clear the global voice override so the new
  // persona starts with its own configured default voice instead of inheriting a
  // selection made inside a different persona's conversation.
  useEffect(() => {
    setSelectedVoiceId(null)
    voiceInitializedRef.current = false
  }, [conversationPersonaId, setSelectedVoiceId])

  // Determine which voice to use: user selection > persona config > default
  // Note: selectedVoiceId is NOT in dependencies to avoid loops - we use the ref for tracking
  useEffect(() => {
    if (voices.length === 0) return

    // Priority 1: User has manually selected a voice in settings
    if (voiceFeatures.selectedVoiceId) {
      const userVoiceExists = voices.some(v => v.id === voiceFeatures.selectedVoiceId)
      if (userVoiceExists) {
        setVoiceId(voiceFeatures.selectedVoiceId)
        voiceInitializedRef.current = true
        return
      }
    }

    // Priority 2: Active persona has a voice configured
    if (activePersona?.voiceConfig?.voiceId) {
      const personaVoiceExists = voices.some(v => v.id === activePersona.voiceConfig?.voiceId)
      if (personaVoiceExists) {
        setVoiceId(activePersona.voiceConfig.voiceId)
        voiceInitializedRef.current = true
        return
      }
    }

    // Priority 3: Set first available voice only if we haven't initialized yet
    if (!voiceInitializedRef.current && voices.length > 0) {
      setVoiceId(voices[0].id)
      voiceInitializedRef.current = true
    }
  }, [voiceFeatures.selectedVoiceId, activePersona?.voiceConfig?.voiceId, voices, setVoiceId])

  // STT hook
  const {
    isSupported: sttSupported,
    isEnabled: sttEnabled,
    setEnabled: setSttEnabledLocal,
    isListening,
    transcript,
    error: sttError,
    toggleListening,
  } = useSpeechToText({
    onError: (error) => {
      console.error('[STT Error]:', error)
      addToast({
        type: 'error',
        title: 'Voice Input Error',
        description: error.message,
        duration: 6000,
      })
    },
  })

  // Streaming TTS hook
  const {
    isSpeaking: streamingTtsSpeaking,
    isBuffering: streamingTtsBuffering,
    processToken: streamingProcessToken,
    flush: flushStreamingTts,
    finish: finishStreamingTts,
    interrupt: interruptStreamingTts,
    prepareForNewStream: prepareStreamingTts,
    connect: connectStreamingTts,
  } = useStreamingTTS({
    voiceId: selectedVoiceId || '',
    enabled: ttsEnabled && voiceFeatures.useStreamingTts && !!selectedVoiceId,
    modelId: activePersona?.voiceConfig?.modelId,
    autoMode: voiceFeatures.streamingConfig.autoMode,
    outputFormat:
      activePersona?.voiceConfig?.streamingOutputFormat ||
      voiceFeatures.streamingConfig.defaultOutputFormat,
    useGlobalEndpoint: voiceFeatures.streamingConfig.useGlobalEndpoint,
    preBufferMs: voiceFeatures.streamingConfig.preBufferMs,
    pronunciationDictionaryLocators: combinedLocators,
    onStart: () => voiceTTSStartedRef.current(),
    onEnd: () => voiceTTSFinishedRef.current(),
    onError: (error) => console.error('[Streaming TTS Error]:', error),
  })

  // Local TTS (Kokoro) is used only where the deployment has it (SA-1392):
  // there, a Kokoro persona speaks through it automatically, regardless of the
  // ElevenLabs-oriented ttsEnabled setting, and any persona does with voice
  // on. A Kokoro persona elsewhere takes the ordinary TTS path.
  const personaUsesLocalTTS = activePersona?.voiceConfig?.provider === 'kokoro'
  const useLocalTTSMode = localTtsMode({
    localTts,
    personaUsesKokoro: personaUsesLocalTTS,
    ttsEnabled,
  })

  // Local TTS hook (Kokoro)
  const {
    isSpeaking: localTtsSpeaking,
    isBuffering: localTtsBuffering,
    processToken: localProcessToken,
    flush: flushLocalTts,
    finish: finishLocalTts,
    interrupt: interruptLocalTts,
    prepareForNewStream: prepareLocalTts,
  } = useLocalTTS({
    voice: activePersona?.voiceConfig?.provider === 'kokoro'
      ? (activePersona.voiceConfig.voiceId || 'af_heart')
      : (edgeLocalConfig?.tts?.defaultVoice || 'af_heart'),
    enabled: useLocalTTSMode,
    basePath: edgeLocalConfig?.tts?.basePath || '/local-tts',
    sampleRate: edgeLocalConfig?.tts?.sampleRate || 24000,
    onStart: () => voiceTTSStartedRef.current(),
    onEnd: () => voiceTTSFinishedRef.current(),
    onError: (error) => console.error('[Local TTS Error]:', error),
  })
  // Local TTS that speaks, buffers or has sentences on their way is active
  // whatever the mode: switching to a non-Kokoro conversation with voice off
  // turns the mode off while Kokoro still speaks (SA-1397).
  const localTtsActive = localTtsSpeaking || localTtsBuffering
  const activeTtsProcessToken = useLocalTTSMode ? localProcessToken : streamingProcessToken
  const activeTtsFlush = useLocalTTSMode ? flushLocalTts : flushStreamingTts
  const activeTtsFinish = useLocalTTSMode ? finishLocalTts : finishStreamingTts
  const activeTtsInterrupt = useLocalTTSMode ? interruptLocalTts : interruptStreamingTts
  const activeTtsPrepare = useLocalTTSMode ? prepareLocalTts : prepareStreamingTts

  // Sync STT enabled state with settings
  useEffect(() => {
    setSttEnabledLocal(voiceFeatures.sttEnabled)
  }, [voiceFeatures.sttEnabled, setSttEnabledLocal])

  // URL refresh hook for expired source URLs
  const { refreshUrl } = useSourceUrl()

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [currentConversation?.messages, isStreaming])

  // Voice interruption: stop TTS when user starts speaking
  useEffect(() => {
    if (sttEnabled && isListening) {
      // User is speaking, interrupt TTS
      if (localTtsActive) {
        interruptLocalTts()
      } else if (voiceFeatures.useStreamingTts && (streamingTtsSpeaking || streamingTtsBuffering)) {
        interruptStreamingTts()
      } else if (ttsSpeaking) {
        stopSpeaking()
      }
    }
  }, [
    sttEnabled,
    isListening,
    localTtsActive,
    interruptLocalTts,
    voiceFeatures.useStreamingTts,
    streamingTtsSpeaking,
    streamingTtsBuffering,
    ttsSpeaking,
    interruptStreamingTts,
    stopSpeaking,
  ])

  // Voice Off or Stop speaking: the voice turn ends as a reply's end ends it,
  // and nothing more of the reply in flight is spoken, while its text keeps
  // arriving (SA-1435, owner ruling 2 Nov). The stream is never cancelled.
  const silenceReply = useCallback(() => {
    if (currentRunRef.current) currentRunRef.current.speechStopped = true
    voiceTTSFinishedRef.current()
  }, [])

  // Handle TTS toggle. Routes through `setEffectiveTtsEnabled` so chat-only
  // personas get a per-persona override (their default is OFF) while
  // voice/avatar personas continue to toggle the global flag.
  const handleTtsToggle = useCallback(async (enabled: boolean) => {
    setEffectiveTtsEnabled(enabled)
    setTtsEnabledLocal(enabled)
    if (!enabled) {
      stopSpeaking()
      interruptLocalTts()
      if (voiceFeatures.useStreamingTts) {
        interruptStreamingTts()
      }
      silenceReply()
    } else if (voiceFeatures.useStreamingTts) {
      // CRITICAL: Initialize AudioContext during user gesture (toggle click)
      // This satisfies browser autoplay policy - must be called directly from click handler
      // Pass force=true because React state hasn't updated yet (setEffectiveTtsEnabled is async)
      try {
        await connectStreamingTts(true)
      } catch (error) {
        console.error('[TTS Toggle] Failed to connect streaming TTS:', error)
      }
    }
  }, [setEffectiveTtsEnabled, setTtsEnabledLocal, stopSpeaking, interruptLocalTts, voiceFeatures.useStreamingTts, interruptStreamingTts, connectStreamingTts, silenceReply])

  // Handle TTS stop (works for all TTS modes). Local TTS is stopped whenever
  // it is active, in or out of its mode (SA-1397).
  const handleTtsStop = useCallback(() => {
    if (useLocalTTSMode || localTtsActive) interruptLocalTts()
    if (!useLocalTTSMode) {
      if (voiceFeatures.useStreamingTts) {
        interruptStreamingTts()
      } else {
        stopSpeaking()
      }
    }
    silenceReply()
  }, [useLocalTTSMode, localTtsActive, interruptLocalTts, voiceFeatures.useStreamingTts, interruptStreamingTts, stopSpeaking, silenceReply])

  // Determine if currently speaking (any mode; local TTS whatever the mode)
  const isCurrentlySpeaking =
    localTtsActive ||
    (!useLocalTTSMode &&
      (voiceFeatures.useStreamingTts ? streamingTtsSpeaking || streamingTtsBuffering : ttsSpeaking))

  // Handle STT toggle
  const handleSttToggle = useCallback((enabled: boolean) => {
    setSttEnabled(enabled)
    setSttEnabledLocal(enabled)
  }, [setSttEnabled, setSttEnabledLocal])

  // Handle voice selection
  const handleVoiceSelect = useCallback((voiceId: string) => {
    setSelectedVoiceId(voiceId)
    setVoiceId(voiceId)
  }, [setSelectedVoiceId, setVoiceId])

  // Handle source click to open viewer
  const handleSourceClick = useCallback((source: DisplaySource) => {
    setSelectedSource(source)
    setIsViewerOpen(true)
  }, [])

  // Handle source viewer navigation
  const handleSourceNavigate = useCallback(
    (direction: 'prev' | 'next') => {
      if (!selectedSource || activeSources.length === 0) return

      const currentIndex = activeSources.findIndex(
        (s) => s.id === selectedSource.id
      )
      if (currentIndex === -1) return

      let newIndex: number
      if (direction === 'prev') {
        newIndex = currentIndex > 0 ? currentIndex - 1 : activeSources.length - 1
      } else {
        newIndex = currentIndex < activeSources.length - 1 ? currentIndex + 1 : 0
      }

      setSelectedSource(activeSources[newIndex])
    },
    [selectedSource, activeSources]
  )

  // Close source viewer
  const handleCloseViewer = useCallback(() => {
    setIsViewerOpen(false)
    // Delay clearing selected source for animation
    setTimeout(() => setSelectedSource(null), 200)
  }, [])

  // One send, run under handleSendMessage's guard. It records what it made
  // on `run`, so handleSendMessage can settle the turn however it ends.
  const runSend = useCallback(
    async (content: string, run: SendRun, opts?: SendOptions) => {
      // Prepare TTS for new response (interrupts current playback, resets state)
      // IMPORTANT: This must be called during user gesture to initialize AudioContext
      // Both are set before the prepare is awaited, so a prepare that rejects
      // still leaves the hook and the voice-off rule able to end the turn.
      // voiceOff is not the complement of the prepare condition: the
      // whole-message path prepares nothing either, yet its speak() runs at
      // the reply's end, so that turn ends from the speech (SA-1435).
      run.voiceOff = !useLocalTTSMode && !ttsEnabled
      // The whole-message voice speaks only a reply that finished, and a new
      // send stops one still playing, as prepare stops the streaming voices
      // (SA-1435): its end can then never land on this send's turn.
      run.wholeMessage = !useLocalTTSMode && ttsEnabled && !voiceFeatures.useStreamingTts
      stopSpeaking()
      if (useLocalTTSMode || (ttsEnabled && voiceFeatures.useStreamingTts)) {
        run.finishTts = activeTtsFinish
        await activeTtsPrepare()
      }

      // Ensure we have a conversation
      let conversationId = currentConversationId
      if (!conversationId) {
        // Create conversation via backend API with no persona
        // (user should use New Conversation modal to select a persona)
        try {
          conversationId = await createConversationOnBackend(null)
        } catch (error) {
          console.error('Failed to create conversation:', error)
          // Fallback to local creation
          conversationId = createConversation(null)
        }
      }
      run.conversationId = conversationId

      // Add user message locally (+ attachment metadata when staged). A send
      // with its own turn (Continue) leaves a staged file on the composer.
      const fileToSend = attachmentsEnabled && opts?.completionMessage === undefined ? stagedFile : null
      const fileMime = fileToSend ? resolveAttachmentMime(fileToSend) : null

      // What the completions request sends as the CURRENT user turn. An
      // attachment-only send has empty `content`, and the backend appends the
      // current user message to the override history unconditionally — an empty
      // string would reach upstream as a blank user turn. Send the same
      // `[sent <kind>]` placeholder the history mapping below uses. The locally
      // displayed message and the persisted message content stay ''. A Continue
      // brings its own turn (SA-1241).
      let completionMessage = opts?.completionMessage ?? content

      if (fileToSend && fileMime) {
        const fileKind = attachmentKind(fileMime)
        if (!content) completionMessage = `[sent ${fileKind}]`
        setStagedFile(null)
        setAttachmentError(null)
        setIsPreparingAttachment(true)
        const userMessageId = addMessage(conversationId, {
          role: 'user',
          content,
          mediaType: fileKind,
          attachment: {
            filename: fileToSend.name,
            mimeType: fileMime,
            byteSize: fileToSend.size,
          },
        })
        try {
          // Resolved mime, not the browser's file.type — Chrome reports ''
          // for .md and application/vnd.ms-excel for .csv, both of which the
          // backend allowlist 415s (final-review fix wave, F1).
          const fd = createUploadFormData(fileToSend, fileMime)
          const up = await apiClient.fetch('/api/media/upload', {
            method: 'POST',
            body: fd,
          })
          if (!up.ok) {
            throw new Error(
              up.status === 413
                ? 'File is too large.'
                : up.status === 415
                  ? 'This file type is not supported.'
                  : "Couldn't upload attachment. Please try again.",
            )
          }
          const { media_ref: mediaRef } = await up.json()
          // Awaited on purpose: the backend computes the AI context at
          // persist time; the completion below reads it from the DB.
          const saved = await syncAttachmentMessage(conversationId, {
            content,
            mediaRef,
            filename: fileToSend.name,
            mimeType: fileMime,
          })
          updateMessage(conversationId, userMessageId, {
            mediaUrl: saved.media_url ?? undefined,
          })
        } catch (err) {
          // Spec §3.2: upload/persist errors keep the staged file and abort
          // the send — the turn stays retryable. Drop the optimistic bubble
          // (it never reached the backend and would otherwise hide the typed
          // text behind an error marker), put the file back on the composer,
          // restore what the user typed, and surface the reason on the chip.
          removeMessage(conversationId, userMessageId)
          setStagedFile(fileToSend)
          if (content) setPendingInputMessage(content)
          setAttachmentError(
            err instanceof Error ? err.message : 'Attachment failed to send.',
          )
          setIsPreparingAttachment(false)
          return
        }
        setIsPreparingAttachment(false)
      } else {
        addMessage(conversationId, { role: 'user', content })
        // Sync user message to backend (fire and forget)
        syncMessageToBackend(conversationId, { role: 'user', content })
      }

      // Add placeholder assistant message
      const assistantMessageId = addMessage(conversationId, {
        role: 'assistant',
        content: '',
        isStreaming: true,
      })
      run.assistantMessageId = assistantMessageId

      setStreaming(true, assistantMessageId)

      // Build conversation history (excluding the user message and assistant placeholder we just added)
      const conversation = useChatStore.getState().conversations[conversationId]
      const history = conversation.messages
        .slice(0, -2) // Exclude the user message and assistant placeholder we just added
        .filter((m) => m.role !== 'system')
        .map((m) => ({
          role: m.role as 'user' | 'assistant',
          content: m.content || (m.mediaType ? `[sent ${m.mediaType}]` : ''),
        }))

      try {
        const connector = getRagConnector()

        // Read connection settings fresh from store to avoid stale closure values
        // (e.g., during initial load before Zustand persist rehydration)
        const currentConnections = useSettingsStore.getState().connections

        // Use persona-specific RAG server URL if configured, otherwise use the
        // deployment-level base (RAG_PROXY_BASE; was connections.ragServerUrl).
        const serverUrl = ragConfig.serverUrl || getRagProxyBase()
        connector.setBaseUrl(serverUrl)

        // Browser-side key: only the no-persona branch below (direct
        // `streamChatCompletion` to nginx) sends it. Persona-bound turns go
        // through `streamChatViaBackend`, where the backend resolves the key
        // (SA-342: persona key → user's credential → env). The no-persona
        // branch's future is ruled under SA-343.
        const apiKey = ragConfig.apiKey || currentConnections.ragApiKey || ''
        connector.setApiKey(apiKey)

        // Resolve fresh persona handle (avoids stale closures during a
        // mid-stream persona switch) — used by both the server-canonical
        // path (we just need the id) and the legacy path (we need the
        // rendered system prompt).
        const freshConversation = useChatStore.getState().conversations[conversationId]
        const freshPersonaId = freshConversation?.personaId
        const freshPersona = freshPersonaId ? usePersonaStore.getState().personas[freshPersonaId] : null
        const freshRagConfig = freshPersona?.ragConfig ?? defaultRagConfig

        let collectedSources: RAGSource[] = []
        let fullAssistantContent = ''

        // Voice-track split (spec 2026-07-28-voice-track-display-speech-split):
        // when the persona opted in AND voice is on, the reply opens with a
        // <voice>...</voice> spoken track. The router sends voice tokens to
        // TTS only and the remainder to the display only; non-compliant
        // replies pass through to both — byte-identical to the old wiring.
        // Includes the streaming-TTS hook's own `!!selectedVoiceId`
        // enablement term (mirrors `useStreamingTTS({ enabled: ... })`
        // above) — without it, TTS-on-but-no-voice-resolved turns would
        // send `voice_active: true` and route the gloss text to a disabled
        // processToken, silently dropping it from speech (final-review fix
        // wave, F6).
        const voiceOutputActive =
          useLocalTTSMode ||
          (ttsEnabled && voiceFeatures.useStreamingTts && !!selectedVoiceId)
        const voiceRouter = createVoiceTrackRouter({
          onDisplay: (text) => {
            fullAssistantContent += text
            appendToMessage(conversationId!, assistantMessageId, text)
          },
          onSpeech: (text) => {
            if (!run.speechStopped) activeTtsProcessToken(text)
          },
          speechEnabled: voiceOutputActive,
        })

        // P2.T0 callback table — shared between server-canonical and
        // legacy paths so behavior is identical (token streaming, TTS,
        // cleanup, backend sync). Defined once to keep the two branches
        // narrow and obviously-equivalent.
        const streamCallbacks = {
          onToken: (token: string) => {
            voiceRouter.push(token)
          },
          onSources: (sources: RAGSource[]) => {
            collectedSources = sources
            const displaySources = transformRAGSources(sources)
            setActiveSources(displaySources)
          },
          // A workflow answered this turn: say so under the reply (SA-474). Live only, never synced.
          onWorkflow: (workflow: ReplyWorkflow) => {
            updateMessage(conversationId!, assistantMessageId, { workflow })
          },
          onComplete: (_fullContent: string, end?: { cut: ReplyCut | null }) => {
            run.completed = true
            voiceRouter.finalize()
            const cleanContent = fullAssistantContent
              .replace(/<think>[\s\S]*?<\/think>/g, '')
              .replace(/^[\s\S]*?<\/think>\s*/g, '')
              .replace(/<\/?voice>/g, '')
              .trim()
            // A reply that stopped early keeps its cut, here and saved (SA-1241).
            const cut = end?.cut ? { cut: end.cut } : {}
            updateMessage(conversationId!, assistantMessageId, {
              isStreaming: false,
              content: cleanContent,
              sources: collectedSources,
              ...cut,
            })
            setStreaming(false)
            syncMessageToBackend(conversationId!, {
              role: 'assistant',
              content: cleanContent,
              sources: collectedSources,
              ...cut,
            })
            if (!run.speechStopped && (useLocalTTSMode || (ttsEnabled && voiceFeatures.useStreamingTts))) {
              activeTtsFlush()
            }
            const spokenText =
              run.wholeMessage && !run.speechStopped ? sanitizeTextForTTS(cleanContent).trim() : ''
            if (spokenText) {
              run.spoken = true
              void speak(spokenText)
            }
          },
          onError: (error: Error) => {
            voiceRouter.finalize()
            updateMessage(conversationId!, assistantMessageId, {
              isStreaming: false,
              error: error.message,
            })
            setStreaming(false)
            syncMessageToBackend(conversationId!, {
              role: 'assistant',
              content: fullAssistantContent,
              error: error.message,
            })
            // Deliberately no activeTtsFlush() on a failed turn (SA-230):
            // flushing would voice the buffered tail — usually a dangling
            // sentence fragment — after the failure. Audio stops at the
            // last word already sent to TTS; the error is surfaced
            // visually, and the next turn's activeTtsPrepare() resets the
            // text processor so the dropped tail can't leak into it.
            // The TTS reply is ended without the tail when the send ends
            // (handleSendMessage, SA-1396), not here: a named tool error is
            // one the stream goes on after, to onComplete, and the rest of
            // that answer is still spoken.
          },
          onApprovalRequired: (event: ApprovalRequiredEvent) => {
            useApprovalToastStore.getState().surface(event)
          },
          onToolCallDelta: (_deltas: ToolCallDelta[]) => {
            // No UI yet; exercised by the parser path so tool-call indicator can
            // be wired to ChatMessage in a follow-up.
          },
          onToolResult: (_event: ToolResultEvent) => {
            // Full tool-result rendering lives in a Phase 4 surface.
          },
        }

        // P2.T0: server-canonical chat path. v2 runtime is core — the
        // server-canonical path is the default whenever a persona is
        // bound. POST to /api/chat/completions and let the backend run
        // compose() + open the upstream stream. This is the boundary
        // tool loops (Phase 2+) need to enforce policy / audit / budget
        // on the server side. Falls through to the direct-to-RAG path
        // only when no persona is bound (e.g. unconfigured chat).
        if (freshPersonaId) {
          await connector.streamChatViaBackend(
            {
              personaId: freshPersonaId,
              conversationId,
              message: completionMessage,
              history,
              modelOverride: ragConfig.model,
              temperature: ragConfig.temperature,
              maxTokens: ragConfig.maxTokens,
              topK: ragConfig.topK,
              scoreThreshold: ragConfig.scoreThreshold,
              voiceActive: voiceOutputActive,
            },
            streamCallbacks,
          )
          return
        }

        const systemPrompt = await fetchSystemPrompt(freshRagConfig, freshPersonaId ?? null)
        const messages = connector.buildMessages(
          systemPrompt,
          history,
          completionMessage
        )

        await connector.streamChatCompletion(
          {
            messages,
            model: ragConfig.model,
            temperature: ragConfig.temperature,
            max_tokens: ragConfig.maxTokens,
            top_k: ragConfig.topK,
            score_threshold: ragConfig.scoreThreshold,
            stream: true,
          },
          streamCallbacks,
          { graphId: freshRagConfig.graphId },
        )
      } catch (error) {
        updateMessage(conversationId, assistantMessageId, {
          isStreaming: false,
          error: error instanceof Error ? error.message : 'An error occurred',
        })
        setStreaming(false)
      }
    },
    [
      currentConversationId,
      ragConfig,
      ragProxyBase,
      connections.ragApiKey,
      createConversation,
      createConversationOnBackend,
      syncMessageToBackend,
      syncAttachmentMessage,
      attachmentsEnabled,
      stagedFile,
      addMessage,
      appendToMessage,
      updateMessage,
      removeMessage,
      setStreaming,
      ttsEnabled,
      voiceFeatures.useStreamingTts,
      useLocalTTSMode,
      selectedVoiceId,
      activeTtsPrepare,
      activeTtsProcessToken,
      activeTtsFlush,
      activeTtsFinish,
      speak,
      stopSpeaking,
    ]
  )

  // The send guard is held for the whole send and released in one place
  // (SA-1395): a cancel fires no callback, so a guard released only by the
  // stream's callbacks stayed set after Stop and every later send was dropped.
  // A send whose stream never completed (a failure, Stop, any other cancel)
  // ends its TTS reply there too, local or ElevenLabs, without the unspoken
  // tail (SA-1392, SA-1396): what was sent plays out, then the voice engine
  // hears the turn end from the TTS hook (SA-1395, SA-1402). A send with voice
  // off prepared no reply, so nothing can start late and the turn ends here.
  // Resolves false when the guard refused the send (a transcript's caller ends
  // its own turn), true once the send has ended. `opts.completionMessage` is a
  // Continue's turn (SA-1241); every other send passes the content alone.
  const handleSendMessage = useCallback(
    async (content: string, opts?: SendOptions): Promise<boolean> => {
      // Guard against re-entry (e.g., rapid Enter key before isStreaming updates)
      if (isSendingRef.current) return false
      isSendingRef.current = true
      const run: SendRun = {
        conversationId: null,
        assistantMessageId: null,
        completed: false,
        finishTts: null,
        voiceOff: false,
        wholeMessage: false,
        spoken: false,
        speechStopped: false,
      }
      currentRunRef.current = run
      try {
        await runSend(content, run, opts)
      } finally {
        isSendingRef.current = false
        if (currentRunRef.current === run) currentRunRef.current = null
        endTurnIfStreaming(run)
        if (!run.completed && !run.speechStopped) run.finishTts?.()
        // Nothing will be spoken: the turn's speech is over here. A spoken
        // whole-message reply ends it from its own end instead.
        if (run.voiceOff || (run.wholeMessage && !run.spoken)) voiceTTSFinishedRef.current()
      }
      return true
    },
    [runSend]
  )

  // Continue a cut reply (SA-1241): the person's bubble and saved message read
  // "Continue", and the turn sent repeats the question the reply answered, so
  // the knowledge search stays on its subject. Offered on the last message only.
  const handleContinue = useCallback(() => {
    const state = useChatStore.getState()
    const messages = state.currentConversationId
      ? state.conversations[state.currentConversationId]?.messages ?? []
      : []
    const question = continueQuestion(messages, messages.length - 1)
    void handleSendMessage(CONTINUE_LABEL, { completionMessage: continueMessage(question) })
  }, [handleSendMessage])

  // Stop only cancels: the aborted send's own end settles the turn and its
  // TTS reply (handleSendMessage), as for a barge-in or any other cancel.
  const handleCancelStream = useCallback(() => {
    getRagConnector().cancelStream()
  }, [])

  // -----------------------------------------------------------------------
  // Realtime mode: user transcript → add to chat only (no RAG call)
  // -----------------------------------------------------------------------

  const handleRealtimeUserTranscript = useCallback(
    async (content: string) => {
      let conversationId = currentConversationId
      if (!conversationId) {
        try {
          conversationId = await createConversationOnBackend(null)
        } catch {
          conversationId = createConversation(null)
        }
      }
      addMessage(conversationId, { role: 'user', content })
      syncMessageToBackend(conversationId, { role: 'user', content })
    },
    [
      currentConversationId,
      createConversationOnBackend,
      createConversation,
      addMessage,
      syncMessageToBackend,
    ]
  )

  // Realtime mode: AI response text → add as assistant message
  const handleAssistantTranscript = useCallback(
    (content: string) => {
      const conversationId = currentConversationId
      if (!conversationId) return

      addMessage(conversationId, { role: 'assistant', content })
      syncMessageToBackend(conversationId, { role: 'assistant', content })
    },
    [currentConversationId, addMessage, syncMessageToBackend]
  )

  // Route voice transcript based on engine type
  const handleVoiceTranscript = useCallback(
    (text: string) => {
      if (voiceConversationSettings.engine === 'openai-realtime') {
        handleRealtimeUserTranscript(text)
      } else {
        // A refused transcript's words were dropped and no reply will answer
        // them: the turn ends, and the person is told (SA-1435).
        void handleSendMessage(text).then((taken) => {
          if (taken) return
          voiceTTSFinishedRef.current()
          addToast({
            type: 'info',
            title: 'Not sent',
            description: 'A reply was still coming. Say it again when it finishes.',
          })
        })
      }
    },
    [
      voiceConversationSettings.engine,
      handleRealtimeUserTranscript,
      handleSendMessage,
      addToast,
    ]
  )

  // Voice conversation hook (pipeline: mic→Deepgram→RAG→TTS, realtime: mic→WebRTC→OpenAI)
  const voiceConv = useVoiceConversation({
    onFinalTranscript: handleVoiceTranscript,
    onAssistantTranscript: handleAssistantTranscript,
    onError: (error) => {
      console.error('[Voice Conversation Error]:', error)
      addToast({
        type: 'error',
        title: 'Voice Conversation Error',
        description: error.message,
        duration: 6000,
      })
    },
    onInterruptTTS: () => {
      // Barge-in (pipeline mode): user spoke while AI was speaking. Local
      // TTS is stopped whenever it is active, in or out of its mode (SA-1397).
      if (localTtsActive && !useLocalTTSMode) interruptLocalTts()
      if (voiceFeatures.useStreamingTts || useLocalTTSMode) {
        activeTtsInterrupt()
      } else {
        stopSpeaking()
      }
      getRagConnector().cancelStream()
    },
    edgeLocalConfig,
    personaSttLanguage: activePersona?.voiceConfig?.sttLanguage ?? null,
    conversationSttLanguageOverride: sttLanguageOverride,
  })

  // Derived: voice conversation is fully active (enabled + has API credentials)
  const voiceConversationActive = voiceConversationSettings.enabled && voiceConv.hasCredentials

  // Auto-disable basic STT when voice conversation becomes active
  useEffect(() => {
    if (voiceConversationActive && sttEnabled) {
      handleSttToggle(false)
      addToast({
        type: 'info',
        title: 'Browser Voice Input Disabled',
        description: 'Using voice conversation (Deepgram) instead.',
        duration: 4000,
      })
    }
    // Only react to voiceConversationActive changes — not sttEnabled
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voiceConversationActive])

  // Switch from voice conversation to basic STT (called from confirmation dialog)
  const handleSwitchToBasicStt = useCallback(async () => {
    // Stop open-mic if active
    if (voiceConv.isOpenMicActive) await voiceConv.toggleOpenMic()
    // Cancel any active voice conversation
    if (voiceConv.state !== 'idle') voiceConv.cancel()
    // Disable voice conversation in settings
    useSettingsStore.getState().setVoiceConversation({ enabled: false })
    // Enable basic STT
    handleSttToggle(true)
    setShowSwitchToSttDialog(false)
    addToast({
      type: 'info',
      title: 'Switched to Browser Voice Input',
      description: 'Voice conversation disabled. Re-enable in Settings.',
      duration: 4000,
    })
  }, [voiceConv, handleSttToggle, addToast])

  // Bridge streaming TTS playback events to voice conversation engine
  useEffect(() => {
    voiceTTSStartedRef.current = voiceConv.notifyTTSStarted
    voiceTTSFinishedRef.current = voiceConv.notifyTTSFinished
  }, [voiceConv.notifyTTSStarted, voiceConv.notifyTTSFinished])

  return (
    <div className={cn('flex h-full flex-row relative', className)}>
      {/* Main chat area */}
      <div className="flex-1 flex flex-col min-w-0 relative">
        {/* Voice control bar */}
        {showVoiceControls && (
          <VoiceControlBar
            ttsEnabled={ttsEnabled}
            onTtsToggle={handleTtsToggle}
            ttsSpeaking={isCurrentlySpeaking}
            ttsBuffering={localTtsBuffering || (!useLocalTTSMode && voiceFeatures.useStreamingTts && streamingTtsBuffering)}
            ttsReady={ttsReady || useLocalTTSMode || localTtsActive}
            onTtsStop={handleTtsStop}
            sttEnabled={sttEnabled}
            onSttToggle={handleSttToggle}
            sttSupported={sttSupported}
            sttListening={isListening}
            sttError={sttError}
            voices={voices}
            selectedVoiceId={selectedVoiceId}
            onVoiceSelect={handleVoiceSelect}
            personaVoiceId={activePersona?.voiceConfig?.voiceId}
            isStreamingMode={voiceFeatures.useStreamingTts}
            voiceConversationActive={voiceConversationActive}
            voiceConversationState={voiceConv.state}
            voiceConversationMode={voiceConversationSettings.mode}
            isOpenMicActive={voiceConv.isOpenMicActive}
            isSpeechDetected={voiceConv.isSpeechDetected}
            onSwitchToBasicStt={() => setShowSwitchToSttDialog(true)}
            voiceEngineType={voiceConv.engineType}
            sttLanguageOverride={sttLanguageOverride}
            personaSttLanguage={activePersona?.voiceConfig?.sttLanguage ?? null}
            onSttLanguageChange={setSttLanguageOverride}
          />
        )}

        {/* Messages area */}
        <div className="flex-1 overflow-y-auto">
          {!currentConversation || currentConversation.messages.length === 0 ? (
            <EmptyState
              suggestedQuestions={suggestedQuestions}
              onSuggestionClick={handleSuggestionClick}
              personaName={activePersona?.name}
            />
          ) : (
            <div className="py-4">
              {currentConversation.messages.map((message, index, all) => (
                <ExperienceMessageBubble
                  key={message.id}
                  message={message}
                  isStreaming={
                    isStreaming && message.id === streamingMessageId
                  }
                  onFeedback={submitMessageFeedback}
                  showFeedback={message.role === 'assistant'}
                  onContinue={
                    message.role === 'assistant' &&
                    message.cut?.continuable &&
                    !message.isStreaming &&
                    !isStreaming &&
                    index === all.length - 1
                      ? handleContinue
                      : undefined
                  }
                />
              ))}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Input area with voice conversation overlay */}
        <div className="relative">
          {/* Voice transcript overlay (floats above input) */}
          {voiceConversationSettings.enabled && voiceConv.hasCredentials && (
            <VoiceTranscriptOverlay
              state={voiceConv.state}
              transcript={voiceConv.partialTranscript}
              audioLevel={voiceConv.audioLevel}
              showTranscript={voiceConversationSettings.showTranscript}
              isOpenMicActive={voiceConv.isOpenMicActive}
              isSpeechDetected={voiceConv.isSpeechDetected}
            />
          )}

          <ChatInput
            onSend={handleSendMessage}
            onCancel={handleCancelStream}
            isLoading={isStreaming}
            disabled={isPreparingAttachment}
            attachmentsEnabled={attachmentsEnabled}
            stagedFile={stagedFile}
            onStageFile={handleStageFile}
            onRemoveStagedFile={handleRemoveStagedFile}
            attachmentError={attachmentError}
            placeholder={
              activePersona
                ? `Ask ${activePersona.name}...`
                : 'Type your message...'
            }
            sttEnabled={sttEnabled}
            isListening={isListening}
            voiceTranscript={transcript}
            onToggleListening={toggleListening}
            hideBasicSttMic={voiceConversationActive}
            externalMessage={pendingInputMessage}
            onExternalMessageConsumed={() => setPendingInputMessage('')}
            leftAddon={
              voiceConversationSettings.enabled && voiceConv.hasCredentials ? (
                <VoiceConversationButton
                  state={voiceConv.state}
                  audioLevel={voiceConv.audioLevel}
                  mode={voiceConversationSettings.mode}
                  isOpenMicActive={voiceConv.isOpenMicActive}
                  isSpeechDetected={voiceConv.isSpeechDetected}
                  onStartListening={voiceConv.startListening}
                  onStopListening={voiceConv.stopListening}
                  onInterrupt={voiceConv.interrupt}
                  onCancel={voiceConv.cancel}
                  onToggleOpenMic={voiceConv.toggleOpenMic}
                  disabled={isStreaming}
                />
              ) : undefined
            }
          />
        </div>

        {/* Toggle button when the source panel is hidden */}
        {!experienceSourcesVisible && activeSources.length > 0 && (
          <SourcePanelToggle
            sourceCount={activeSources.length}
            onClick={() => setExperienceSourcesVisible(true)}
            position="right"
            label="Sources"
          />
        )}
      </div>

      {/* Right-side resizable source panel (toggleable drawer) */}
      {experienceSourcesVisible && activeSources.length > 0 && (
        <div className="relative shrink-0" style={{ width: experiencePanel.size }}>
          {/* Drag handle */}
          <div
            className={cn(
              'absolute left-0 top-0 bottom-0 w-1 cursor-col-resize z-10 hover:bg-indigo-400/40 transition-colors',
              experiencePanel.isDragging && 'bg-indigo-400/60'
            )}
            onMouseDown={experiencePanel.handleMouseDown}
          />
          <div className="h-full border-l border-border overflow-hidden">
            <ExperienceSourcePanel
              sources={activeSources}
              onOpenSource={handleSourceClick}
              onClose={() => setExperienceSourcesVisible(false)}
            />
          </div>
        </div>
      )}

      {/* Source viewer modal */}
      <ExperienceSourceViewer
        source={selectedSource}
        sources={activeSources}
        isOpen={isViewerOpen}
        onClose={handleCloseViewer}
        onNavigate={handleSourceNavigate}
        onRefreshUrl={refreshUrl}
        ragServerUrl={activePersona?.ragConfig.serverUrl || ragProxyBase}
        ragApiKey={activePersona?.ragConfig.apiKey || connections.ragApiKey}
        hasPrev={
          selectedSource
            ? activeSources.findIndex((s) => s.id === selectedSource.id) > 0
            : false
        }
        hasNext={
          selectedSource
            ? activeSources.findIndex((s) => s.id === selectedSource.id) <
              activeSources.length - 1
            : false
        }
      />

      {/* In-chat approval toast — singleton, driven by useApprovalToastStore */}
      <ApprovalToast />

      {/* Switch voice input method confirmation dialog */}
      <Modal open={showSwitchToSttDialog} onOpenChange={setShowSwitchToSttDialog}>
        <ModalContent size="sm">
          <ModalHeader>
            <ModalTitle>Switch Voice Input Method?</ModalTitle>
            <ModalDescription>
              You are currently using voice conversation (Deepgram STT).
            </ModalDescription>
          </ModalHeader>
          <ModalBody>
            <div className="space-y-2 text-sm text-foreground">
              <p>Switching to basic voice input will:</p>
              <ul className="list-disc list-inside space-y-1 ml-1">
                <li>Stop any active voice conversation</li>
                <li>Disable Deepgram speech recognition</li>
                <li>Enable browser-native speech recognition</li>
              </ul>
              <p className="text-muted-foreground pt-1">
                You can re-enable voice conversation in Settings.
              </p>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={() => setShowSwitchToSttDialog(false)}>
              Cancel
            </Button>
            <Button variant="default" onClick={handleSwitchToBasicStt}>
              Switch to Basic
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  )
}

// Default fallback suggestions when API doesn't return any
const DEFAULT_SUGGESTIONS = [
  'What are the main features of the product?',
  'How do I get started with the API?',
  'Explain the architecture overview',
]

// Empty state component
interface EmptyStateProps {
  suggestedQuestions: string[]
  onSuggestionClick: (questionText: string) => void
  personaName?: string
}

function EmptyState({ suggestedQuestions, onSuggestionClick, personaName }: EmptyStateProps) {
  const displayQuestions = suggestedQuestions.length > 0
    ? suggestedQuestions
    : DEFAULT_SUGGESTIONS

  return (
    <div className="flex flex-col items-center justify-center h-full p-8 text-center">
      <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
        <MessageSquare className="w-8 h-8 text-primary" />
      </div>
      <h3 className="text-lg font-medium text-foreground mb-2">
        Start a Conversation
      </h3>
      <p className="text-muted-foreground max-w-sm mb-6">
        Ask questions about your knowledge base and get AI-powered answers with
        source citations.
      </p>
      <div className="flex flex-col gap-2 text-sm text-muted-foreground">
        <p>Try asking{personaName ? ` ${personaName}` : ''}:</p>
        <div className="space-y-2">
          {displayQuestions.map((question, index) => (
            <button
              key={index}
              onClick={() => onSuggestionClick(question)}
              className="px-4 py-2 bg-muted rounded-lg hover:bg-accent hover:text-primary transition-colors text-left w-full"
            >
              "{question}"
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
